import { useMemo, useState } from 'react';
import { db } from '../db/indexedDb';
import { SearchBar } from '../components/SearchBar';
import { SongList } from '../components/SongList';
import { usePlaylists, useTracks } from '../hooks/useIndexedDb';
import { usePlayer } from '../hooks/usePlayer';
import { useToast } from '../hooks/useToast';
import type { Track } from '../types';

const EMPTY_TRACKS: Track[] = [];

export function SongsPage() {
  const trackQuery = useTracks();
  const tracks = trackQuery ?? EMPTY_TRACKS;
  const playlists = usePlaylists() ?? [];
  const { enqueue, playTrack } = usePlayer();
  const { pushToast } = useToast();
  const [query, setQuery] = useState('');
  const [artist, setArtist] = useState('all');
  const [album, setAlbum] = useState('all');
  const [sortBy, setSortBy] = useState<'title' | 'artist' | 'album' | 'recent' | 'duration'>('title');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [targetPlaylistId, setTargetPlaylistId] = useState('');

  const artists = useMemo(() => Array.from(new Set(tracks.map((track) => track.artist))).sort(), [tracks]);
  const albums = useMemo(() => Array.from(new Set(tracks.map((track) => track.album))).sort(), [tracks]);

  const filteredTracks = useMemo(() => {
    return [...tracks]
      .filter((track) => {
        const matchesQuery = [track.title, track.artist, track.album].some((field) =>
          field.toLowerCase().includes(query.toLowerCase())
        );
        const matchesArtist = artist === 'all' || track.artist === artist;
        const matchesAlbum = album === 'all' || track.album === album;
        return matchesQuery && matchesArtist && matchesAlbum;
      })
      .sort((a, b) => {
        if (sortBy === 'recent') return b.createdAt.localeCompare(a.createdAt);
        if (sortBy === 'duration') return b.duration - a.duration;
        return a[sortBy].localeCompare(b[sortBy]);
      });
  }, [album, artist, query, sortBy, tracks]);

  const toggleTrack = (trackId: string) => {
    setSelectedIds((current) =>
      current.includes(trackId) ? current.filter((id) => id !== trackId) : [...current, trackId]
    );
  };

  const addSelectionToPlaylist = async () => {
    const playlist = playlists.find((item) => item.id === targetPlaylistId);
    if (!playlist || !selectedIds.length) return;
    const nextTrackIds = Array.from(new Set([...playlist.trackIds, ...selectedIds]));
    await db.updatePlaylist({ ...playlist, trackIds: nextTrackIds });
    pushToast({
      title: 'Playlist updated',
      description: `${selectedIds.length} tracks added to ${playlist.name}.`
    });
    setSelectedIds([]);
  };

  return (
    <section className="space-y-6">
      <div className="grid gap-4 xl:grid-cols-[2fr,repeat(3,minmax(0,1fr))]">
        <SearchBar value={query} onChange={setQuery} />
        <select
          value={artist}
          onChange={(event) => setArtist(event.target.value)}
          className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm"
          aria-label="Filter songs by artist"
        >
          <option value="all">All artists</option>
          {artists.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <select
          value={album}
          onChange={(event) => setAlbum(event.target.value)}
          className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm"
          aria-label="Filter songs by album"
        >
          <option value="all">All albums</option>
          {albums.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <select
          value={sortBy}
          onChange={(event) => setSortBy(event.target.value as typeof sortBy)}
          className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm"
          aria-label="Sort songs"
        >
          <option value="title">Sort: Title</option>
          <option value="artist">Sort: Artist</option>
          <option value="album">Sort: Album</option>
          <option value="recent">Sort: Recent</option>
          <option value="duration">Sort: Duration</option>
        </select>
      </div>

      <div className="panel flex flex-col gap-4 rounded-[2rem] p-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-accent">Bulk actions</p>
          <p className="mt-2 text-sm text-foreground/70">
            {selectedIds.length} selected. Add them to the queue or save them into a playlist.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => {
              enqueue(selectedIds);
              pushToast({ title: 'Queue updated', description: `${selectedIds.length} tracks added.` });
            }}
            className="pill"
            disabled={!selectedIds.length}
          >
            Add to queue
          </button>
          <select
            value={targetPlaylistId}
            onChange={(event) => setTargetPlaylistId(event.target.value)}
            className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm"
            aria-label="Choose playlist for selected songs"
          >
            <option value="">Choose playlist</option>
            {playlists.map((playlist) => (
              <option key={playlist.id} value={playlist.id}>
                {playlist.name}
              </option>
            ))}
          </select>
          <button type="button" onClick={() => void addSelectionToPlaylist()} className="rounded-full bg-accent px-4 py-2 text-sm font-semibold text-black">
            Add to playlist
          </button>
        </div>
      </div>

      <SongList
        tracks={filteredTracks}
        selectedIds={selectedIds}
        onToggleSelect={toggleTrack}
        onPlay={(trackId) => void playTrack(trackId, filteredTracks.map((track) => track.id))}
        onQueue={(trackId) => {
          enqueue([trackId]);
          pushToast({ title: 'Queued', description: 'Track added to the queue.' });
        }}
      />
    </section>
  );
}
